import React from "react";
import Modal from "../modal/Modal";
import Box from "../patterns/Box";
import Flex from "../patterns/Flex";
import Text from "../patterns/Text";
import ButtonPill from "../patterns/ButtonPill";
import { ChatMessage } from "../../pages/Create";
import { StoryCharacter } from "../messages/ChatInputField";
import { VideoRenderer, VideoMessage } from "./VideoRenderer";
import { logEvent } from "firebase/analytics";
import { analytics } from "../../utils/firebase";

interface VideoExporterProps {
  messages: ChatMessage[];
  characters: StoryCharacter[];
  title: string;
  hideModal: () => void;
}

enum ExportState {
  idle = "idle",
  rendering = "rendering",
  complete = "complete",
  error = "error",
}

const TYPING_FRAMES = 45; // ~1.5s at 30fps
const DISPLAY_FRAMES = 20; // ~0.67s pause between messages
const END_FRAMES = 90; // 3s hold at end
const FPS = 30;

const VideoExporter = ({
  messages,
  characters,
  title,
  hideModal,
}: VideoExporterProps) => {
  const [state, setState] = React.useState<ExportState>(ExportState.idle);
  const [progress, setProgress] = React.useState(0);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    };
  }, [downloadUrl]);

  const extractText = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join(" ");
    if (React.isValidElement(node)) {
      if (node.type === "br") return "\n";
      return extractText((node.props as any).children);
    }
    return "";
  };

  const convertMessages = (): VideoMessage[] => {
    return messages
      .map((msg) => {
        const text = msg.messageStrings
          ? msg.messageStrings
              .filter((s) => !s.includes("data:image") && s.trim() !== "\\n")
              .join(" ")
              .trim()
          : extractText(msg.message).trim();

        return {
          text,
          characterName: msg.character?.name,
          characterId: msg.character?.id,
          isReceiver: msg.character?.isReceiver || false,
          isNarrative: !msg.character,
          isImage: msg.renderAsImageMessage || false,
          imageSrc: msg.imageSrc,
        };
      })
      .filter((m) => m.text.length > 0 || m.isImage);
  };

  const preloadImages = async (): Promise<Map<string, HTMLImageElement>> => {
    const imageMap = new Map<string, HTMLImageElement>();
    const promises: Promise<void>[] = [];

    characters.forEach((char) => {
      if (char.photo) {
        const p = new Promise<void>((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            imageMap.set(char.id, img);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = char.photo!;
        });
        promises.push(p);
      }
    });

    await Promise.all(promises);
    return imageMap;
  };

  const startExport = async () => {
    setState(ExportState.rendering);
    setProgress(0);
    cancelledRef.current = false;
    logEvent(analytics, "video_export_start");

    try {
      const videoMessages = convertMessages();
      if (videoMessages.length === 0) {
        setState(ExportState.error);
        return;
      }

      const characterPhotos = await preloadImages();
      const renderer = new VideoRenderer();
      const canvas = renderer.getCanvas();

      // Check for MediaRecorder support
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";

      const stream = canvas.captureStream(FPS);
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 5_000_000,
      });

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        if (cancelledRef.current) return;
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
        setState(ExportState.complete);
        logEvent(analytics, "video_export_complete");
      };

      mediaRecorder.start(100); // collect data every 100ms

      // State machine for frame rendering
      let msgIndex = 0;
      let frameInPhase = 0;
      let phase: "typing" | "display" | "end" = "typing";
      const visibleIndices: number[] = [];
      const totalFrames =
        videoMessages.length * (TYPING_FRAMES + DISPLAY_FRAMES) + END_FRAMES;
      let frameCount = 0;

      const renderFrame = () => {
        if (cancelledRef.current) {
          mediaRecorder.stop();
          return;
        }

        // Calculate scroll offset
        const showTyping = phase === "typing" && msgIndex < videoMessages.length;
        const scrollOffset = renderer.calculateScrollOffset(
          videoMessages,
          visibleIndices,
          showTyping
        );

        // Draw scene
        renderer.drawBackground();
        renderer.drawHeader(title);
        renderer.drawWatermark();

        // Draw visible messages with scroll
        const ctx = canvas.getContext("2d")!;
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 120, 1080, 1920 - 220); // clip to content area
        ctx.clip();
        ctx.translate(0, -scrollOffset);

        let y = 120 + 20; // start below header
        for (let i = 0; i < visibleIndices.length; i++) {
          const idx = visibleIndices[i];
          const msg = videoMessages[idx];
          const prevMsg = idx > 0 ? videoMessages[idx - 1] : null;
          const nextIdx = i + 1 < visibleIndices.length ? visibleIndices[i + 1] : -1;
          const nextMsg = nextIdx >= 0 ? videoMessages[nextIdx] : null;

          const showName =
            !msg.isNarrative &&
            (idx === 0 || msg.characterId !== prevMsg?.characterId);
          const showAvatar =
            !msg.isNarrative &&
            (!nextMsg || msg.characterId !== nextMsg.characterId);

          // Gap between message groups
          const isNewGroup = idx > 0 && msg.characterId !== prevMsg?.characterId;
          if (isNewGroup && i > 0) {
            y += 16;
          } else if (i > 0) {
            y += 6;
          }

          const avatar = msg.characterId
            ? characterPhotos.get(msg.characterId)
            : undefined;

          y = renderer.drawMessage(msg, y, showName, showAvatar, avatar);
        }

        // Draw typing indicator
        if (phase === "typing" && msgIndex < videoMessages.length) {
          const currentMsg = videoMessages[msgIndex];
          const prevMsg =
            visibleIndices.length > 0
              ? videoMessages[visibleIndices[visibleIndices.length - 1]]
              : null;
          const isNewGroup = prevMsg && currentMsg.characterId !== prevMsg.characterId;

          y += isNewGroup || visibleIndices.length === 0 ? 16 : 6;
          renderer.drawTypingIndicator(y, currentMsg.isReceiver, frameInPhase);
        }

        ctx.restore();

        // Advance state
        frameInPhase++;

        if (phase === "typing") {
          if (frameInPhase >= TYPING_FRAMES) {
            phase = "display";
            frameInPhase = 0;
            visibleIndices.push(msgIndex);
          }
        } else if (phase === "display") {
          if (frameInPhase >= DISPLAY_FRAMES) {
            msgIndex++;
            if (msgIndex >= videoMessages.length) {
              phase = "end";
              frameInPhase = 0;
            } else {
              phase = "typing";
              frameInPhase = 0;
            }
          }
        } else if (phase === "end") {
          if (frameInPhase >= END_FRAMES) {
            mediaRecorder.stop();
            return;
          }
        }

        frameCount++;
        setProgress(Math.min(99, Math.round((frameCount / totalFrames) * 100)));
        requestAnimationFrame(renderFrame);
      };

      requestAnimationFrame(renderFrame);
    } catch (err) {
      console.error("Video export error:", err);
      setState(ExportState.error);
    }
  };

  const fileName = `${(title || "story").replace(/[^a-zA-Z0-9]/g, "-")}.webm`;

  return (
    <Modal
      title="Export Video"
      subtitle="Create a vertical video for TikTok or Reels"
      hideModal={hideModal}
    >
      <Flex
        style={{
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          padding: 24,
          minHeight: 200,
        }}
      >
        {state === ExportState.idle && (
          <>
            <Text style={{ textAlign: "center", marginBottom: 8 }}>
              Export {messages.length} messages as a 9:16 vertical video with
              animated typing indicators and a dark theme.
            </Text>
            <Text
              variant="meta"
              style={{ textAlign: "center", marginBottom: 20 }}
            >
              Includes a "Made with TinyFiction.com" watermark.
            </Text>
            <ButtonPill variant="primary" onClick={startExport}>
              Start Export
            </ButtonPill>
          </>
        )}

        {state === ExportState.rendering && (
          <>
            <Text style={{ marginBottom: 16 }}>Rendering video...</Text>
            <Box
              style={{
                width: "100%",
                height: 8,
                borderRadius: 4,
                background: "rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <Box
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "#0D6EFD",
                  borderRadius: 4,
                  transition: "width 0.3s",
                }}
              />
            </Box>
            <Text variant="meta" style={{ marginTop: 8 }}>
              {progress}% complete
            </Text>
          </>
        )}

        {state === ExportState.complete && downloadUrl && (
          <>
            <Text style={{ marginBottom: 16, fontWeight: 600 }}>
              Video ready!
            </Text>
            <a
              href={downloadUrl}
              download={fileName}
              style={{ textDecoration: "none" }}
            >
              <ButtonPill variant="primary">Download Video</ButtonPill>
            </a>
            <Text
              variant="meta"
              style={{ marginTop: 16, textAlign: "center", maxWidth: 300 }}
            >
              The file is .webm format. Most platforms accept this directly, or
              you can convert to .mp4 with a free online tool.
            </Text>
          </>
        )}

        {state === ExportState.error && (
          <>
            <Text style={{ color: "red", marginBottom: 12 }}>
              Something went wrong during export.
            </Text>
            <ButtonPill
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setState(ExportState.idle);
                setProgress(0);
              }}
            >
              Try Again
            </ButtonPill>
          </>
        )}
      </Flex>
    </Modal>
  );
};

export default VideoExporter;
